// src/pages/AdminContacts.jsx
import React, { useEffect, useState } from "react";
import { listContacts, getContact, replyContact } from "../services/AdminContactService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function AdminContacts() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null); // contact detail
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async (p = page) => {
    try {
      setLoading(true);
      const data = await listContacts({ page: p, limit: 20, q });
      setRows(data.rows || []);
      setPages(data.pages || 1);
      setPage(data.page || p);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openContact = async (id) => {
    try {
      setSelected(await getContact(id));
      setReplyText("");
    } catch (e) {
      toast.error("Failed to load contact");
    }
  };

  const doReply = async () => {
    if (!replyText.trim()) return toast.error("Please type a reply message");
    setReplying(true);
    try {
      const res = await replyContact(selected.id, replyText.trim());
      toast.success("Reply sent");
      setSelected(res.contact);
      setReplyText("");
      // refresh list in background
      load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to send reply");
    } finally {
      setReplying(false);
    }
  };

  return (
    <div className="container py-4">
      <h3>Contact Messages</h3>

      <div className="d-flex gap-2 mb-3">
        <input className="form-control" placeholder="Search name / email / text" value={q} onChange={(e)=>setQ(e.target.value)} />
        <button className="btn btn-primary" onClick={()=>load(1)}>Search</button>
        <button className="btn btn-outline-secondary" onClick={()=>{ setQ(""); load(1); }}>Clear</button>
      </div>

      <div className="card mb-3">
        <div className="card-body p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Message (preview)</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td style={{maxWidth: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{r.message}</td>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary" onClick={()=>openContact(r.id)}>View / Reply</button>
                  </td>
                </tr>
              ))}
              {rows.length===0 && (
                <tr><td colSpan="6" className="text-center py-3">{loading ? "Loading…" : "No messages"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <div>Page {page} / {pages}</div>
        <div>
          <button className="btn btn-sm btn-outline-secondary me-2" disabled={page<=1} onClick={()=>{ setPage(p=>Math.max(1,p-1)); load(page-1); }}>Prev</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={page>=pages} onClick={()=>{ setPage(p=>p+1); load(page+1); }}>Next</button>
        </div>
      </div>

      {/* Detail + Reply panel */}
      {selected && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <strong>{selected.name}</strong> — <small>{selected.email}</small>
            </div>
            <div>
              <button className="btn btn-sm btn-outline-secondary" onClick={()=>setSelected(null)}>Close</button>
            </div>
          </div>
          <div className="card-body">
            <p><strong>Message:</strong></p>
            <div className="border rounded p-3 mb-3" style={{whiteSpace:"pre-wrap"}}>{selected.message}</div>

            <p><strong>Replies</strong></p>
            <div>
              { (selected.replies || []).length === 0 ? <div className="text-muted">No replies yet</div> :
                selected.replies.map(r => (
                  <div key={r.id} className="mb-2">
                    <div className="small text-muted">{r.createdAt ? new Date(r.createdAt).toLocaleString() : ""} {r.admin ? `by ${r.admin.name || "admin"}` : ""}</div>
                    <div className="p-2 bg-light rounded">{r.message}</div>
                  </div>
                ))
              }
            </div>

            <hr />
            <div>
              <textarea className="form-control mb-2" rows="4" placeholder="Type your reply here..." value={replyText} onChange={(e)=>setReplyText(e.target.value)}></textarea>
              <div className="d-flex gap-2">
                <button className="btn btn-primary" disabled={replying} onClick={doReply}>{replying ? "Sending…" : "Send Reply"}</button>
                <button className="btn btn-outline-secondary" onClick={()=>setReplyText("")}>Clear</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
